import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@/components/form/Select';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { getGroupSummary, getLedgerMonthly, type GroupSummaryData, type LedgerStat } from '@/api/accountsReports';
import type { ExportCell } from '../exportReport';
import { Check, LedgerVouchersView, Loading, PeriodBar, ReportFrame } from './reportParts';
import { MONTH_NAMES, amt, drcr, num, r2, tdate, td, th, useExcel, useKeyNav, usePeriod, NO_ACTS } from './reportUtils';

const CB_GROUPS = ['Cash-in-Hand', 'Bank Accounts', 'Bank OD A/c'];

/** Ledgers under one of these groups (at any depth). */
function ledgersUnder(d: GroupSummaryData, names: string[]) {
  const byId = new Map(d.groups.map((g) => [g.id, g]));
  const topOf = (gid: number) => { for (let g = byId.get(gid), i = 0; g && i < 50; g = g.parentId ? byId.get(g.parentId) : undefined, i++) if (names.includes(g.name)) return g.name; return null; };
  return names.map((n) => ({ group: n, ledgers: d.ledgers.filter((l) => topOf(l.groupId) === n).sort((a, b) => a.name.localeCompare(b.name)) })).filter((x) => x.ledgers.length);
}

/** Cash Book — the cash ledger's vouchers with a running balance, as Tally's Cash Book. */
export function CashBookPage() {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'group-summary', p.from, p.to], queryFn: () => getGroupSummary(p) });
  const cash = useMemo(() => (q.data ? ledgersUnder(q.data, ['Cash-in-Hand']).flatMap((x) => x.ledgers) : []), [q.data]);
  const [pick, setPick] = useState<number | null>(null);
  const id = pick ?? cash.find((l) => l.name === 'Cash')?.id ?? cash[0]?.id;
  return (
    <Loading q={q}>
      {cash.length > 1 && (
        <FilterBar>
          <FilterField label="Cash ledger" className="col-span-3 sm:w-56">
            <Select value={id} onChange={(e) => setPick(Number(e.target.value))}>{cash.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Select>
          </FilterField>
        </FilterBar>
      )}
      {id ? <LedgerVouchersView key={id} ledgerId={id} from={p.from} to={p.to} /> : <p className="py-8 text-center text-sm text-[var(--text-muted)]">No cash ledger.</p>}
    </Loading>
  );
}

/**
 * Cash/Bank Book — Tally's Cash/Bank Summary: each cash and bank ledger under its group with its
 * balance; a ledger opens its Monthly Summary, a month its vouchers.
 */
export function CashBankBookPage() {
  const [p] = usePeriod();
  const [op, setOp] = useState(false);
  const [tx, setTx] = useState(false);
  const [led, setLed] = useState<number | null>(null);
  const [month, setMonth] = useState<{ from: string; to: string } | null>(null);
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'group-summary', p.from, p.to], queryFn: () => getGroupSummary(p) });
  const groups = useMemo(() => (q.data ? ledgersUnder(q.data, CB_GROUPS) : []), [q.data]);
  type Line = { label: string; s: Pick<LedgerStat, 'op' | 'dr' | 'cr' | 'cl'>; group: boolean; id?: number };
  const lines: Line[] = useMemo(() => groups.flatMap((g) => {
    const s = g.ledgers.reduce((a, l) => ({ op: r2(a.op + l.op), dr: r2(a.dr + l.dr), cr: r2(a.cr + l.cr), cl: r2(a.cl + l.cl) }), { op: 0, dr: 0, cr: 0, cl: 0 });
    return [{ label: g.group, s, group: true }, ...g.ledgers.map((l) => ({ label: l.name, s: l, group: false, id: l.id }))];
  }), [groups]);
  const acts = useMemo(() => lines.map((l) => (l.id ? () => setLed(l.id!) : null)), [lines]);
  const { row } = useKeyNav(led == null ? acts : NO_ACTS);
  const excel = useExcel();

  if (led != null && month) return <LedgerVouchersView ledgerId={led} from={month.from} to={month.to} onBack={() => setMonth(null)} />;
  if (led != null) return <LedgerMonthlyView ledgerId={led} onBack={() => setLed(null)} onMonth={setMonth} />;

  const cols: [string, 'op' | 'tx' | 'cl'][] = [...(op ? [['Opening Balance', 'op'] as [string, 'op']] : []), ...(tx ? [['Transactions', 'tx'] as [string, 'tx']] : []), ['Closing Balance', 'cl']];
  const pair = (s: Line['s'], k: 'op' | 'tx' | 'cl'): [number, number] => (k === 'tx' ? [s.dr, s.cr] : [Math.max(s[k], 0), Math.max(-s[k], 0)]);
  const tops = lines.filter((l) => l.group);
  const tot = cols.map(([, k]) => tops.reduce((a, l) => { const [d, c] = pair(l.s, k); return [r2(a[0] + d), r2(a[1] + c)]; }, [0, 0]));
  return (
    <>
      <PeriodBar onExcel={lines.length ? () => excel('Cash_Bank_Summary', 'Cash Bank Summary', ['Particulars', ...cols.flatMap(([l]) => [`${l} Dr`, `${l} Cr`])],
        [...lines.map((l) => [l.group ? l.label : `   ${l.label}`, ...cols.flatMap(([, k]) => pair(l.s, k).map((x) => x || null))] as ExportCell[]), ['Grand Total', ...tot.flat()]]) : undefined}>
        <Check label="Opening balance" checked={op} onChange={setOp} />
        <Check label="Transactions" checked={tx} onChange={setTx} />
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame title="Cash/Bank Summary" right={`${tdate(p.from)} to ${tdate(p.to)}`} note="Click a ledger for its monthly summary — or use ↑ ↓ and Enter.">
          <table className="w-full text-xs" style={{ minWidth: 220 + cols.length * 220 }}>
            <thead>
              <tr className="border-b border-[var(--border-color)]">
                <th rowSpan={2} className={`${th} text-left align-bottom`}>Particulars</th>
                {cols.map(([l]) => <th key={l} colSpan={2} className={`${th} text-center`}>{l}</th>)}
              </tr>
              <tr className="border-b border-[var(--border-color)]">{cols.map(([l]) => [<th key={`${l}d`} className={`${th} text-right`}>Debit</th>, <th key={`${l}c`} className={`${th} text-right`}>Credit</th>])}</tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.label}-${i}`} {...row(i)}>
                  <td className={`${td} ${l.group ? 'font-bold' : 'pl-6 italic'}`}>{l.label}</td>
                  {cols.map(([, k]) => pair(l.s, k).map((x, j) => <td key={`${k}${j}`} className={`${num} ${l.group ? 'font-bold' : ''}`}>{amt(x)}</td>))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--text-primary)] font-extrabold">
                <td className={td}>Grand Total</td>
                {tot.map(([d, c], i) => [<td key={`d${i}`} className={num}>{amt(d)}</td>, <td key={`c${i}`} className={num}>{amt(c)}</td>])}
              </tr>
            </tfoot>
          </table>
        </ReportFrame>
      </Loading>
    </>
  );
}

/** Ledger Monthly Summary: opening, then each month's debits, credits and closing balance. */
function LedgerMonthlyView({ ledgerId, onBack, onMonth }: { ledgerId: number; onBack: () => void; onMonth: (m: { from: string; to: string }) => void }) {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'monthly', ledgerId, p.from, p.to], queryFn: () => getLedgerMonthly(ledgerId, p) });
  const d = q.data;
  const acts = useMemo(() => [null, ...(d?.months ?? []).map((m) => () => onMonth({ from: m.from, to: m.to }))], [d, onMonth]);
  const { row } = useKeyNav(acts, onBack);
  const excel = useExcel();
  const totDr = (d?.months ?? []).reduce((s, m) => s + m.dr, 0), totCr = (d?.months ?? []).reduce((s, m) => s + m.cr, 0);
  return (
    <>
      <PeriodBar onBack={onBack} onExcel={d ? () => excel(`Monthly_${d.ledger.name}`, 'Monthly Summary', ['Particulars', 'Debit', 'Credit', 'Closing Balance'],
        [['Opening Balance', Math.max(d.opening, 0) || null, Math.max(-d.opening, 0) || null, null], ...d.months.map((m) => [MONTH_NAMES[Number(m.month.slice(5)) - 1] + ' ' + m.month.slice(0, 4), m.dr || null, m.cr || null, m.closing] as ExportCell[])]) : undefined} />
      <Loading q={q}>
        {d && (
          <ReportFrame title={`Ledger: ${d.ledger.name}`} right={`Monthly Summary · ${tdate(d.from)} to ${tdate(d.to)}`} note="Click a month for its vouchers — or use ↑ ↓ and Enter, Esc to go back.">
            <table className="w-full min-w-[520px] text-xs">
              <thead>
                <tr className="border-b border-[var(--border-color)]">
                  <th className={`${th} text-left`}>Particulars</th><th className={`${th} text-right`}>Debit</th><th className={`${th} text-right`}>Credit</th><th className={`${th} text-right`}>Closing Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr {...row(0, 'font-bold')}>
                  <td className={td}>Opening Balance</td><td className={num}>{d.opening > 0 ? amt(d.opening) : ''}</td><td className={num}>{d.opening < 0 ? amt(d.opening) : ''}</td><td className={num} />
                </tr>
                {d.months.map((m, i) => (
                  <tr key={m.month} {...row(i + 1)}>
                    <td className={td}>{MONTH_NAMES[Number(m.month.slice(5)) - 1]}</td>
                    <td className={num}>{amt(m.dr)}</td><td className={num}>{amt(m.cr)}</td><td className={num}>{drcr(m.closing)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--text-primary)] font-extrabold">
                  <td className={td}>Grand Total</td>
                  <td className={num}>{amt(totDr + Math.max(d.opening, 0))}</td><td className={num}>{amt(totCr + Math.max(-d.opening, 0))}</td><td className={num}>{drcr(d.closing)}</td>
                </tr>
              </tfoot>
            </table>
          </ReportFrame>
        )}
      </Loading>
    </>
  );
}
